// Regras compartilhadas da tela de Catálogos do WhatsApp. O backend repete as
// mesmas validações antes de enviar qualquer produto à Meta.

export const AVAILABILITIES = ['in stock', 'out of stock'];
export const CONDITIONS = ['new', 'refurbished', 'used'];
export const RETAILER_ID_FORMAT = /^[A-Za-z0-9._-]{1,100}$/;
export const MAX_PRICE_CENTS = 10000000000;
export const PRODUCT_IMAGE_TYPES = ['image/jpeg', 'image/png'];
export const PRODUCT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export const isCloudInbox = inbox =>
  inbox?.channel_type === 'Channel::Whatsapp' &&
  inbox?.provider === 'whatsapp_cloud';

// Mesma política da API: administradores ou perfis com inbox_manage, e somente
// quando a conta possui ao menos uma caixa WhatsApp Cloud.
export const canAccessWhatsappCatalogs = ({
  isAdmin = false,
  permissions = [],
  inboxes = [],
} = {}) =>
  (isAdmin || (permissions || []).includes('inbox_manage')) &&
  (inboxes || []).some(isCloudInbox);

// "12.50" (valor do CurrencyInput) -> 1250. Retorna null para valores inválidos.
export const decimalToCents = value => {
  if (value === '' || value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const [integer, fraction = ''] = text.split('.');
  return Number(integer) * 100 + Number(fraction.padEnd(2, '0'));
};

// 1250 -> "12.50", formato aceito pelo CurrencyInput.
export const centsToDecimal = cents => {
  if (cents === null || cents === undefined || cents === '') return '';
  const amount = Number(cents);
  if (!Number.isInteger(amount) || amount < 0) return '';
  return (amount / 100).toFixed(2);
};

export const formatCents = cents => {
  const amount = Number(cents);
  if (!Number.isFinite(amount)) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount / 100);
};

export const isHttpsUrl = value => {
  const text = String(value || '').trim();
  if (!text || text.length > 2000) return false;
  try {
    const url = new URL(text);
    return (
      url.protocol === 'https:' &&
      !!url.hostname &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
};

export const emptyProduct = () => ({
  id: '',
  retailer_id: '',
  name: '',
  description: '',
  price: '',
  availability: 'in stock',
  condition: 'new',
  image_url: '',
  url: '',
  brand: '',
});

export const productToForm = product => ({
  ...emptyProduct(),
  ...Object.fromEntries(
    Object.entries(product || {}).filter(([, value]) => value !== null)
  ),
  price: centsToDecimal(product?.price_cents),
  availability: AVAILABILITIES.includes(product?.availability)
    ? product.availability
    : 'in stock',
  condition: CONDITIONS.includes(product?.condition)
    ? product.condition
    : 'new',
});

// Retorna um objeto { campo: chave i18n } apenas com os campos inválidos.
export const validateProduct = (form, { isNew = true } = {}) => {
  const errors = {};
  const text = key => String(form?.[key] || '').trim();
  if (isNew && !RETAILER_ID_FORMAT.test(text('retailer_id'))) {
    errors.retailer_id = 'RETAILER_ID';
  }
  if (!text('name') || text('name').length > 150) errors.name = 'NAME';
  if (!text('description') || text('description').length > 5000) {
    errors.description = 'DESCRIPTION';
  }
  const cents = decimalToCents(form?.price);
  if (!cents || cents <= 0 || cents > MAX_PRICE_CENTS) errors.price = 'PRICE';
  if (!AVAILABILITIES.includes(form?.availability)) {
    errors.availability = 'AVAILABILITY';
  }
  if (!CONDITIONS.includes(form?.condition)) errors.condition = 'CONDITION';
  if (!isHttpsUrl(form?.image_url)) errors.image_url = 'IMAGE_URL';
  if (text('url') && !isHttpsUrl(text('url'))) errors.url = 'URL';
  if (text('brand').length > 100) errors.brand = 'BRAND';
  return errors;
};

export const productPayload = form => ({
  retailer_id: String(form.retailer_id || '').trim(),
  name: String(form.name || '').trim(),
  description: String(form.description || '').trim(),
  price_cents: decimalToCents(form.price),
  availability: form.availability,
  condition: form.condition,
  image_url: String(form.image_url || '').trim(),
  url: String(form.url || '').trim(),
  brand: String(form.brand || '').trim(),
});

export const productImageError = file => {
  if (!file) return 'IMAGE_TYPE';
  if (!PRODUCT_IMAGE_TYPES.includes(file.type)) return 'IMAGE_TYPE';
  if (!file.size || file.size > PRODUCT_IMAGE_MAX_BYTES) return 'IMAGE_SIZE';
  return '';
};

export const filterProducts = (products, query) => {
  const normalize = value =>
    String(value || '')
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .toLowerCase();
  const term = normalize(query).trim();
  if (!term) return products;
  return products.filter(product =>
    [product.name, product.retailer_id, product.brand, product.description]
      .map(normalize)
      .some(value => value.includes(term))
  );
};

// "in stock" -> "IN_STOCK" para chaves de tradução.
export const optionKey = value =>
  String(value || '')
    .replace(/\s+/g, '_')
    .toUpperCase();
